import React, { useEffect, useState } from 'react';

import { InputLabel } from '@material-ui/core';
import { InputPaddingCSS } from 'components/MeshList/goals/CreateGoalDescriptionField';
import { getDefaultSelectedOption, StatusSelectDropdown } from 'screens/common/sidepanel/Goals/GoalProgress';
import styled from 'styled-components';
import { Button } from 'styles/admin';
import {
  Divider1,
  FlexContainer,
  hideNumberInputArrowsCSS,
  StyledBorderedPaper,
  StyledTextField,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { Typography12, Typography14 } from 'uiToolkit/Typography';

const GoalCheckInCardFooter = ({ onCheckInClickHandler = () => {}, disabled, onCheckInCancelHandler }) => (
  <VerticallyAlignedFlexContainer style={{ marginTop: '1rem' }}>
    <Button
      size="large"
      blueOutline
      style={{
        width: '6rem',
        padding: '0.5rem',
        fontWeight: '600',
        marginRight: '1rem',
        marginLeft: 'auto',
      }}
      onClick={onCheckInCancelHandler}
      disabled={disabled}
    >
      Cancel
    </Button>
    <Button
      size="large"
      blue
      style={{ width: '6rem', padding: '0.5rem', fontWeight: '600' }}
      onClick={onCheckInClickHandler}
      disabled={disabled}
    >
      Check-in
    </Button>
  </VerticallyAlignedFlexContainer>
);

const GoalCheckInCard = ({
  completedPercentage,
  goalDetails,
  updatedActualValue,
  updateTask = () => {},
  onCheckInCancelHandler = () => {},
  setCheckInCardIsVisible = () => {},
  achieved,
  progressLevel,
  goalClosureStatus,
  pageName,
  measureType = '',
}) => {
  const [checkInCardStatus, setCheckInCardStatus] = useState(
    progressLevel === 'CLOSED' ? getDefaultSelectedOption(goalClosureStatus) : getDefaultSelectedOption(progressLevel)
  );

  const [achievedValue, setAchievedValue] = useState(updatedActualValue);

  const onCheckInClickHandler = () => {
    if (checkInCardStatus.progressLevel === 'CLOSED') {
      updateTask(
        {
          ...goalDetails,
          actual: achievedValue,
          goalProgressLevel: checkInCardStatus.progressLevel,
          goalClosureStatus: checkInCardStatus.value,
        },
        false
      );
    } else {
      updateTask(
        {
          ...goalDetails,
          actual: achievedValue,
          goalProgressLevel: checkInCardStatus.progressLevel,
        },
        false
      );
    }
    setCheckInCardIsVisible(false);
  };

  useEffect(() => {
    setAchievedValue(updatedActualValue);
  }, [updatedActualValue]);

  return (
    <Container>
      <VerticallyAlignedFlexContainer style={{ justifyContent: 'space-between' }}>
        <Typography14 weight={600}>New Check-in</Typography14>
        <Typography14 weight={600}>{completedPercentage}</Typography14>
      </VerticallyAlignedFlexContainer>

      <VerticallyAlignedFlexContainer style={{ marginTop: '1.25rem' }}>
        <FlexContainer style={{ flexDirection: 'column' }}>
          <InputLabel style={{ marginBottom: '0.25rem' }}>
            <Typography12>Achieved {measureType}</Typography12>
          </InputLabel>
          <InputField
            value={achievedValue}
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            onChange={(e) => {
              setAchievedValue(e.target.value);
            }}
          />
        </FlexContainer>
        <FlexContainer style={{ flexDirection: 'column' }}>
          <InputLabel style={{ marginBottom: '0.25rem' }}>
            <Typography12>Status</Typography12>
          </InputLabel>
          <StatusSelectDropdown
            progressLevel={checkInCardStatus?.progressLevel}
            goalClosureStatus={checkInCardStatus?.value}
            updateTask={updateTask}
            goalDetails={goalDetails}
            pageName={pageName}
            customStyles={customStylesForStatusDropdown}
            setCheckInCardStatus={setCheckInCardStatus}
            isCheckInCard
          />
        </FlexContainer>
      </VerticallyAlignedFlexContainer>
      <Divider1 style={{ marginTop: '1rem', marginLeft: '-1rem', marginRight: '-1rem' }} />
      <GoalCheckInCardFooter
        onCheckInClickHandler={onCheckInClickHandler}
        onCheckInCancelHandler={onCheckInCancelHandler}
      />
    </Container>
  );
};

const customStylesForStatusDropdown = {
  dropdownIndicator: (styles, data) => ({
    ...styles,
    color: 'transparent',
    padding: '4px',
  }),
  indicatorSeparator: (styles, data) => ({
    ...styles,
    backgroundColor: 'transparent',
  }),
  control: (styles, data) => {
    // const color = data.getValue()[0].color;
    const color = '#ccc';
    return {
      ...styles,
      backgroundColor: 'white',
      width: '15ch',
      minHeight: '40px',
      boxShadow: 'none',
      // borderColor: 'transparent',
      ':hover': {
        ...styles[':hover'],
        borderColor: color,
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
      ':focus': {
        ...styles[':focus'],
        borderColor: color,
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
      ':active': {
        ...styles[':active'],
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
    };
  },
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const hoverColor = '#F9F9F9';
    const selectedColor = '#F0F0F0';
    return {
      ...styles,
      backgroundColor: isSelected ? selectedColor : isFocused ? hoverColor : null,
      color: data.color,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor: isSelected ? selectedColor : hoverColor,
      },
      ...dot(data.color),
    };
  },
  input: (styles, state) => styles,
  singleValue: (styles, { data }) => ({ ...styles, color: data.color }),
};

const dot = (color = '#ccc') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
  },
});

const Container = styled(StyledBorderedPaper)`
  width: 20rem;
  padding: 1rem;
  box-shadow: 3px 4px 5px rgba(0, 0, 0, 0.25);
`;

const InputField = styled(StyledTextField)`
  .MuiInputBase-root {
    font-size: ${(props) => props.theme.typography.body2.fontSize};
    height: 2.5rem;
  }
  input {
    ${hideNumberInputArrowsCSS};
    ${InputPaddingCSS};
  }
  width: 85%;
`;

export default GoalCheckInCard;
